<lib-modal
  data-testid="import-dialog"
  [closeLabel]="'testResults.upload.close' | translate"
  [title]="'testResults.upload.title' | translate"
  [subtitle]="'testResults.upload.subtitle' | translate"
  [contentTemplate]="inhalt"
  [footerTemplate]="bericht() ? fertig : undefined"
  [visible]="visible()"
  [disabled]="primaerGesperrt()"
  [saveText]="primaerText() | translate"
  [cancelText]="'testResults.upload.cancel' | translate"
  (save)="weiterOderStarten()"
  (cancel)="schliessen()"
></lib-modal>

<ng-template #inhalt>
  @if (bericht(); as ergebnis) {
    <div class="import-bericht" data-testid="import-hinweise" role="status">
      <p class="import-bericht__zahl">
        {{ 'testResults.upload.imported' | translate: { count: ergebnis.importedTests } }}
      </p>
      <div class="import-hinweis">
        <mat-icon aria-hidden="true">info</mat-icon>
        <div class="import-bericht__inhalt">
          <strong>{{ 'testResults.upload.skippedTitle' | translate }}</strong>
          <div class="import-hinweis__text">{{ 'testResults.upload.skippedHint' | translate }}</div>
          <ul class="import-bericht__liste">
            @for (test of ergebnis.skipped; track $index) {
              @let text = grund(test);
              <li data-testid="import-hinweis-test">
                <span class="import-bericht__name">{{ test.testName }}{{ text ? ': ' : '' }}</span>
                {{ text }}
              </li>
            }
          </ul>
        </div>
      </div>
    </div>
  } @else {
    <ol class="import-schritte">
      @for (nummer of schritte; track nummer) {
        <li
          class="import-schritte__schritt"
          [class.import-schritte__schritt--erreicht]="schritt() >= nummer"
          [attr.data-testid]="'import-schritt-' + nummer"
          [attr.aria-current]="schritt() === nummer ? 'step' : null"
        >
          <span class="import-schritte__nummer" aria-hidden="true">{{ nummer }}</span>
          <span>{{ 'testResults.upload.steps.' + nummer | translate }}</span>
        </li>
      }
    </ol>

    @if (schritt() === 1) {
      <div class="import-quellen" role="radiogroup" [attr.aria-label]="'testResults.upload.steps.1' | translate">
        @for (quelle of quellen; track quelle.id) {
          <label
            class="import-quelle"
            [class.import-quelle--aktiv]="format() === quelle.id"
            [class.import-quelle--gesperrt]="quelle.gesperrt"
            [attr.title]="quelle.gesperrt ? ('testResults.upload.locked' | translate) : null"
          >
            <span class="import-quelle__icon" [class]="'import-quelle__icon--' + quelle.id" aria-hidden="true">
              <mat-icon>{{ quelle.icon }}</mat-icon>
            </span>
            <span class="import-quelle__text">
              <span class="import-quelle__titel">
                {{ 'testResults.upload.sources.' + quelle.id + '.label' | translate }}
              </span>
              <span class="import-quelle__beschreibung">
                {{ 'testResults.upload.sources.' + quelle.id + '.desc' | translate }}
              </span>
            </span>
            @if (quelle.gesperrt) {
              <span class="import-quelle__gesperrt">{{ 'testResults.upload.locked' | translate }}</span>
            }
            <input
              type="radio"
              class="import-quelle__radio"
              name="import-quelle"
              [attr.data-testid]="'import-quelle-' + quelle.id"
              [disabled]="quelle.gesperrt"
              [checked]="format() === quelle.id"
              (change)="!quelle.gesperrt && format.set(quelle.id)"
            />
          </label>
        }
      </div>
    } @else {
      <div class="import-ziel">
        @if (errorMessage(); as meldung) {
          <p class="import-fehler" role="alert" data-testid="import-fehler">{{ meldung }}</p>
        }

        <app-file-dropzone
          [accept]="acceptedFileTypes"
          [hint]="'testResults.upload.dropHint' | translate"
          [busy]="busy()"
          (fileSelected)="onFileSelected($event)"
          (cleared)="file.set(null)"
        />

        <div class="import-hinweis" data-testid="import-format">
          <mat-icon aria-hidden="true">shield</mat-icon>
          <div>
            <strong>
              {{
                'testResults.upload.expectedFormat'
                  | translate: { format: 'testResults.upload.formats.' + format() | translate }
              }}
            </strong>
            <div class="import-hinweis__text" [innerHTML]="'testResults.upload.matching' | translate"></div>
          </div>
        </div>

        <div class="import-umgebung">
          <mat-icon aria-hidden="true">settings</mat-icon>
          <label class="import-umgebung__label" for="import-umgebung-auswahl">
            {{ 'testResults.upload.targetEnvironment' | translate }}
          </label>
          @if (umgebungenStatus() === 'fehler') {
            <div class="import-umgebung__meldung" role="alert" data-testid="import-umgebung-fehler">
              {{ 'testResults.upload.environmentsError' | translate }}
              <button
                type="button"
                class="import-umgebung__erneut"
                data-testid="import-umgebung-erneut"
                (click)="ladeUmgebungen()"
              >
                {{ 'testResults.upload.environmentsRetry' | translate }}
              </button>
            </div>
          } @else if (umgebungenStatus() === 'geladen' && umgebungen().length === 0) {
            <p class="import-umgebung__meldung" data-testid="import-umgebung-leer">
              {{ 'testResults.upload.environmentsEmpty' | translate }}
            </p>
          } @else {
            <select
              id="import-umgebung-auswahl"
              class="import-umgebung__auswahl"
              data-testid="import-umgebung"
              [disabled]="umgebungenStatus() === 'laedt'"
              (change)="umgebung.set($any($event.target).value)"
            >
              @for (eintrag of umgebungen(); track eintrag.id) {
                <option [value]="eintrag.name" [selected]="eintrag.name === umgebung()">{{ eintrag.name }}</option>
              }
            </select>
          }
        </div>

        @if (release(); as ziel) {
          <p class="import-release" data-testid="import-release">
            {{ 'testResults.upload.targetRelease' | translate: { release: ziel.label } }}
          </p>
        }
      </div>
    }
  }
</ng-template>

<ng-template #fertig>
  <div class="import-fertig">
    <button #fertigKnopf type="button" lib-button variant="primary" data-testid="import-fertig" (click)="schliessen()">
      {{ 'testResults.upload.done' | translate }}
    </button>
  </div>
</ng-template>
